import { GlCodeAccountType } from '@/types/models';

/**
 * Policy rules about what a GL code is ALLOWED to do. Deliberately pure and
 * free of any `@/api` import so both the Worker handler and the client bundle
 * can share one definition — the render guard and the write guard must be the
 * SAME condition, or you get a field that is hidden but still writable.
 */

/**
 * May this GL code carry `triggersLateFee`?
 *
 * Only INCOME codes. A late fee is revenue assessed for non-payment of revenue,
 * so an expense, bank, or equity code has no business drawing one — and before
 * this rule existed, 360 of 546 live codes in one real chart were flagged.
 *
 * This is NOT enforced by a DB CHECK constraint: `gl_codes` is referenced by
 * `associations`, `charges`, and `journal_lines`, and adding a CHECK needs the
 * 12-step table rebuild that miniflare cannot perform on a referenced table.
 * The API handler is therefore the authority; the UI merely reflects it.
 */
export const canTriggerLateFee = (accountType: string | null | undefined): boolean =>
	accountType === GlCodeAccountType.Income;

/**
 * Account types a customer invoice line (and the Item that prefills one) may
 * credit, in picker display order.
 *
 * Revenue first, then expense: billing a homeowner back for a repair the
 * association paid (a chargeback) credits the EXPENSE the bill debited, so the
 * P&L nets the recovery against the cost instead of grossing up both income
 * and expense. The posting is still DR AR / CR line GL — only the bucket moves.
 */
export const INVOICE_LINE_GL_TYPES: readonly GlCodeAccountType[] = [
	GlCodeAccountType.Income,
	GlCodeAccountType.OtherIncome,
	GlCodeAccountType.Expense,
	GlCodeAccountType.CapitalExpense,
	GlCodeAccountType.OtherExpense,
	GlCodeAccountType.CostOfGoodsSold,
];

/**
 * Account types a vendor BILL line (and a vendor's default GL, which prefills one) may debit,
 * in picker display order.
 *
 * Expense first — what nearly every bill is. Then income: a bill that charges the association
 * against one of its revenue lines (e.g. an "Administrative Late Fee" the management company
 * bills back) posts DR income / CR AP, a balanced contra-revenue entry that nets against the
 * income it reverses instead of inventing an expense. The server never validated type (see
 * `api/accounting/gl-fallback.ts`); only these pickers did, so this list is the whole gate.
 * Bank, AR/AP and equity stay out: a bill reclassified onto those corrupts the balance sheet.
 */
export const BILL_LINE_GL_TYPES: readonly GlCodeAccountType[] = [
	GlCodeAccountType.Expense,
	GlCodeAccountType.CapitalExpense,
	GlCodeAccountType.OtherExpense,
	GlCodeAccountType.CostOfGoodsSold,
	GlCodeAccountType.Income,
	GlCodeAccountType.OtherIncome,
];

/** Membership test for {@link BILL_LINE_GL_TYPES}. */
export const isBillLineGlType = (accountType: string | null | undefined): boolean =>
	(BILL_LINE_GL_TYPES as readonly string[]).includes(accountType ?? '');

/**
 * Bill-line GL codes in picker order: by {@link BILL_LINE_GL_TYPES} rank (expense first), then
 * code. The GL API returns the chart in `sort_order, code` order, where income precedes expense,
 * and the typeahead shows only its first page — unsorted, an ordinary bill would open on a menu of
 * income accounts. Codes outside the policy are dropped.
 */
export const billLineGlOptions = <T extends { accountType?: string | null; code: string }>(codes: readonly T[]): T[] => {
	const rank = (t: string | null | undefined) => (BILL_LINE_GL_TYPES as readonly string[]).indexOf(t ?? '');
	return codes
		.filter((c) => isBillLineGlType(c.accountType))
		.sort((a, b) => rank(a.accountType) - rank(b.accountType) || a.code.localeCompare(b.code));
};
